import "./App.css";
import { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { trackPageView } from './lib/analytics';
import { tokenStorage } from './lib/tokenStorage';
import { Toaster } from './components/ui/sonner';
import { Header } from './components/Header';
import { HeroCarousel } from './components/HeroCarousel';
import { ValuesSection } from './components/ValuesSection';
import { ServicesSection } from './components/ServicesSection';
import { HowItWorksSection } from './components/HowItWorksSection';
import { IndustriesSection } from './components/IndustriesSection';
import { ReviewsSection } from './components/ReviewsSection';
import { CollaborationStrip } from './components/CollaborationStrip';
import { AudioDemoSection } from './components/AudioDemoSection';
import { StatsCounter } from './components/StatsCounter';
import { Footer } from './components/Footer';
import { WhatsAppButton } from './components/WhatsAppButton';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { TermsOfService } from './components/TermsOfService';
import { CookiePolicy } from './components/CookiePolicy';
import { MouseGlow, GrainOverlay, NotchSeparator, TextInterstitial, TerminalStyles } from './components/TerminalEffects';

import { CookieConsent } from './components/CookieConsent';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { TenantProvider } from './contexts/TenantContext';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';

// Admin pages - lazy loaded
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const CompaniesPage = lazy(() => import('./pages/admin/CompaniesPage'));
const UsersPage = lazy(() => import('./pages/admin/UsersPage'));
const AgentsPage = lazy(() => import('./pages/admin/AgentsPage'));
const AdminBillingPage = lazy(() => import('./pages/admin/BillingPage'));
const OnboardingManagement = lazy(() => import('./pages/admin/OnboardingManagement'));
const OnboardingDetail = lazy(() => import('./pages/admin/OnboardingDetail'));
const AdminPricingPage = lazy(() => import('./pages/admin/PricingPage'));
const AdminDocumentsPage = lazy(() => import('./pages/admin/DocumentsPage'));
const ProspectsPage = lazy(() => import('./pages/admin/ProspectsPage'));

// Portal pages - lazy loaded
const PortalDashboard = lazy(() => import('./pages/portal/Dashboard'));
const CallsPage = lazy(() => import('./pages/portal/CallsPage'));
const LeadsPage = lazy(() => import('./pages/portal/LeadsPage'));
const SocialPage = lazy(() => import('./pages/portal/SocialPage'));
const ReportsPage = lazy(() => import('./pages/portal/ReportsPage'));
const SettingsPage = lazy(() => import('./pages/portal/SettingsPage'));
const PortalBillingPage = lazy(() => import('./pages/portal/BillingPage'));
const PortalDocumentsPage = lazy(() => import('./pages/portal/DocumentsPage'));
const PaymentSuccess = lazy(() => import('./pages/portal/PaymentSuccess'));
const PaymentCancel = lazy(() => import('./pages/portal/PaymentCancel'));

// Onboarding pages - lazy loaded
const OnboardingWizard = lazy(() => import('./pages/onboarding/OnboardingWizard'));
const OnboardingSuccess = lazy(() => import('./pages/onboarding/OnboardingSuccess'));

// Blog pages - lazy loaded
const BlogListing = lazy(() => import('./pages/BlogListing'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const BlogManagement = lazy(() => import('./pages/admin/BlogManagement'));

// Lead Pipeline page - lazy loaded
const LeadPipelinePage = lazy(() => import('./pages/LeadPipelinePage'));

// Portal wrapper
import PortalWrapper from './components/layout/PortalWrapper';

const LazyFallback = () => (
  <div className="min-h-screen bg-[#F5F0E0] dark:bg-[#1A1A1A] flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2A2A2A] dark:border-[#D4F7DC]" />
  </div>
);

const Home = () => {
  return (
    <div className="min-h-screen bg-[#F5F0E0] dark:bg-[#1A1A1A]">
      <MouseGlow />
      <GrainOverlay />
      <TerminalStyles />
      <Header />
      <HeroCarousel />
      <NotchSeparator bgTo="#2A2A2A" />
      <AudioDemoSection />
      <NotchSeparator bgTo="#D4F7DC" />
      <ValuesSection />
      <TextInterstitial lines={["We're reinventing", "how businesses", "connect with customers."]} />
      <NotchSeparator bgTo="#FAF8F4" />
      <StatsCounter />
      <NotchSeparator bgTo="#F5F0E0" />
      <ServicesSection />
      <NotchSeparator bgTo="#FAF8F4" />
      <HowItWorksSection />
      <TextInterstitial lines={["AI-native technology", "that never sleeps."]} />
      <IndustriesSection />
      <ReviewsSection />
      <NotchSeparator bgTo="#F5F0E0" />
      <CollaborationStrip />
      <Footer />
      <WhatsAppButton />
      <CookieConsent />
      <Toaster />
    </div>
  );
};

// Protected route wrapper
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading, checkAuth } = useAuth();
  const hasToken = !!tokenStorage.get();
  const [authAttempts, setAuthAttempts] = useState(0);
  
  // If there's a token but no user yet, trigger a re-check (max 3 attempts)
  useEffect(() => {
    if (!loading && !user && hasToken && authAttempts < 3) {
      setAuthAttempts(prev => prev + 1);
      checkAuth();
    }
  }, [loading, user, hasToken, checkAuth, authAttempts]);
  
  // Reset attempts when user is found
  useEffect(() => {
    if (user) setAuthAttempts(0);
  }, [user]);
  
  if (loading || (!user && hasToken && authAttempts < 3)) {
    return (
      <div className="min-h-screen bg-[#F5F0E0] dark:bg-[#1A1A1A] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2A2A2A] dark:border-[#D4F7DC]"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (requireAdmin && user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function PageTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <PageTracker />
        <AuthProvider>
          <ThemeProvider>
            <TenantProvider>
              <Suspense fallback={<LazyFallback />}>
              <Routes>
                {/* Landing Page */}
                <Route path="/" element={<Home />} />
                <Route path="/lead-pipeline" element={<LeadPipelinePage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                
                {/* Blog */}
                <Route path="/blog" element={<BlogListing />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                
                {/* Auth */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                
                {/* Onboarding */}
                <Route path="/onboarding" element={<OnboardingWizard />} />
                <Route path="/onboarding/success" element={<OnboardingSuccess />} />
                
                {/* Admin Routes */}
                <Route path="/admin" element={
                  <ProtectedRoute requireAdmin>
                    <AdminDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/admin/prospects" element={
                  <ProtectedRoute requireAdmin>
                    <ProspectsPage />
                  </ProtectedRoute>
                } />
                <Route path="/admin/onboarding" element={
                  <ProtectedRoute requireAdmin>
                    <OnboardingManagement />
                  </ProtectedRoute>
                } />
                <Route path="/admin/onboarding/:submissionId" element={
                  <ProtectedRoute requireAdmin>
                    <OnboardingDetail />
                  </ProtectedRoute>
                } />
                <Route path="/admin/companies" element={
                  <ProtectedRoute requireAdmin>
                    <CompaniesPage />
                  </ProtectedRoute>
                } />
                <Route path="/admin/users" element={
                  <ProtectedRoute requireAdmin>
                    <UsersPage />
                  </ProtectedRoute>
                } />
                <Route path="/admin/agents" element={
                  <ProtectedRoute requireAdmin>
                    <AgentsPage />
                  </ProtectedRoute>
                } />
                <Route path="/admin/billing" element={
                  <ProtectedRoute requireAdmin>
                    <AdminBillingPage />
                  </ProtectedRoute>
                } />
                <Route path="/admin/blog" element={
                  <ProtectedRoute requireAdmin>
                    <BlogManagement />
                  </ProtectedRoute>
                } />
                <Route path="/admin/pricing" element={
                  <ProtectedRoute requireAdmin>
                    <AdminPricingPage />
                  </ProtectedRoute>
                } />
                <Route path="/admin/documents" element={
                  <ProtectedRoute requireAdmin>
                    <AdminDocumentsPage />
                  </ProtectedRoute>
                } />
                
                {/* Client Portal Routes */}
                <Route path="/portal/:slug" element={
                  <ProtectedRoute>
                    <PortalWrapper>
                      <PortalDashboard />
                    </PortalWrapper>
                  </ProtectedRoute>
                } />
                <Route path="/portal/:slug/calls" element={
                  <ProtectedRoute>
                    <PortalWrapper>
                      <CallsPage />
                    </PortalWrapper>
                  </ProtectedRoute>
                } />
                <Route path="/portal/:slug/leads" element={
                  <ProtectedRoute>
                    <PortalWrapper>
                      <LeadsPage />
                    </PortalWrapper>
                  </ProtectedRoute>
                } />
                <Route path="/portal/:slug/social" element={
                  <ProtectedRoute>
                    <PortalWrapper>
                      <SocialPage />
                    </PortalWrapper>
                  </ProtectedRoute>
                } />
                <Route path="/portal/:slug/reports" element={
                  <ProtectedRoute>
                    <PortalWrapper>
                      <ReportsPage />
                    </PortalWrapper>
                  </ProtectedRoute>
                } />
                <Route path="/portal/:slug/settings" element={
                  <ProtectedRoute>
                    <PortalWrapper>
                      <SettingsPage />
                    </PortalWrapper>
                  </ProtectedRoute>
                } />
                <Route path="/portal/:slug/billing" element={
                  <ProtectedRoute>
                    <PortalWrapper>
                      <PortalBillingPage />
                    </PortalWrapper>
                  </ProtectedRoute>
                } />
                <Route path="/portal/:slug/documents" element={
                  <ProtectedRoute>
                    <PortalWrapper>
                      <PortalDocumentsPage />
                    </PortalWrapper>
                  </ProtectedRoute>
                } />
                
                {/* Payment Result Pages */}
                <Route path="/portal/payment/success" element={<PaymentSuccess />} />
                <Route path="/portal/payment/cancel" element={<PaymentCancel />} />
              </Routes>
              </Suspense>
              <Toaster />
            </TenantProvider>
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
